import React, { useMemo, useState } from "react";
import { Button, Form } from "react-bootstrap";
import {
  WorkflowV2,
  WorkflowState,
  WorkflowTransition,
  WorkflowGuard,
  FieldGuard,
  WorkflowCaptureField,
  WorkflowValue,
  isExecutableWorkflow,
  validateWorkflow,
  upgradeLegacyWorkflow,
  downgradeToLegacy,
  slugifyStateId,
  StateKind,
  TransitionKind,
  CaptureType,
  FieldOp,
} from "./workflow-types";

/**
 * Workflow Builder — graduates the legacy comma-separated workflow chain into
 * an executable v2 state machine (states, transitions, guards, capture
 * fields) that ilearn runs via the server-owned workflow-instance API.
 *
 * Legacy resources keep their simple text input until the author explicitly
 * upgrades; "Revert to simple list" is the escape hatch back.
 */

interface Props {
  workflow: WorkflowValue;
  onChange: (workflow: string[] | WorkflowV2) => void;
  /**
   * Requirement decision steps (clinicalSpec.decisionSequence) states can be
   * tagged with; the studio's Decision Sequence map derives from these tags.
   */
  decisionSteps?: Array<{ step: number; name: string }>;
  /** Role vocabulary for the Actor select (clinical roles master + roles in use).
   *  "system" is always offered; a stored actor outside the list shows flagged. */
  actorOptions?: string[];
  /** §10.2 guardrail claims a guarded transition can declare it implements. */
  guardrailOptions?: Array<{ id: string; trigger: string }>;
}

const KIND_OPTIONS: Array<{ value: StateKind; label: string }> = [
  { value: "start", label: "Start" },
  { value: "task", label: "Task" },
  { value: "decision", label: "Decision" },
  { value: "hold", label: "Hold (paused)" },
  { value: "terminal", label: "Terminal (end)" },
];

const TRANSITION_KIND_OPTIONS: Array<{ value: TransitionKind; label: string }> = [
  { value: "advance", label: "Advance" },
  { value: "hold", label: "Hold" },
  { value: "escalate", label: "Escalate" },
  { value: "return", label: "Return" },
];

const CAPTURE_TYPE_OPTIONS: CaptureType[] = ["text", "textarea", "number", "select", "multiselect", "boolean"];
const FIELD_OPS: FieldOp[] = ["notEmpty", "equals", "notEquals", "gt", "gte", "lt", "lte", "in"];

const TRANSITION_BADGE: Record<TransitionKind, { icon: string; cls: string }> = {
  advance: { icon: "→", cls: "wfb-edge-advance" },
  hold: { icon: "⏸", cls: "wfb-edge-hold" },
  escalate: { icon: "⚠", cls: "wfb-edge-escalate" },
  return: { icon: "↩", cls: "wfb-edge-return" },
};

const WorkflowBuilder: React.FC<Props> = ({ workflow, onChange, decisionSteps, actorOptions, guardrailOptions }) => {
  const [showJson, setShowJson] = useState(false);
  const [jsonDraft, setJsonDraft] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [expandedState, setExpandedState] = useState<string | null>(null);

  const isV2 = isExecutableWorkflow(workflow);
  const def = isV2 ? (workflow as WorkflowV2) : null;
  const errors = useMemo(() => (def ? validateWorkflow(def) : []), [def]);

  // ── Legacy mode: the original comma-separated input + upgrade path ──
  if (!def) {
    const steps = Array.isArray(workflow) ? workflow : [];
    return (
      <Form.Group className="mb-3">
        <div className="cs-sect-head">
          <span className="cs-sect-title">Workflow Steps</span>
          <span className="cs-sect-hint">comma-separated — display only; upgrade to a state machine for guards, capture and execution</span>
        </div>
        <Form.Control
          type="text"
          value={steps.join(", ")}
          onChange={(e) =>
            onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
          }
          placeholder="Patient, Diagnosis, Treatment, Utilization, Outcome, Cost"
        />
        <div className="d-flex align-items-center gap-2 mt-2">
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={steps.length < 2}
            onClick={() => onChange(upgradeLegacyWorkflow(steps))}
          >
            ⚙ Upgrade to state machine
          </Button>
          <Form.Text className="text-muted" style={{ fontSize: 11, marginTop: 0 }}>
            {steps.length < 2
              ? "Enter at least 2 steps to upgrade."
              : "Converts these steps into an executable workflow: real states, transitions, guards, and per-step data capture that iLearn enforces."}
          </Form.Text>
        </div>
      </Form.Group>
    );
  }

  // ── v2 mode: structured builder ──
  const update = (patch: Partial<WorkflowV2>) => onChange({ ...def, ...patch });

  const updateState = (index: number, patch: Partial<WorkflowState>) => {
    const states = def.states.map((s, i) => (i === index ? { ...s, ...patch } : s));
    update({ states });
  };

  const renameStateId = (index: number, newId: string) => {
    const oldId = def.states[index].id;
    if (!newId || newId === oldId) return;
    const states = def.states.map((s, i) => (i === index ? { ...s, id: newId } : s));
    const transitions = def.transitions.map((t) => ({
      ...t,
      from: t.from === oldId ? newId : t.from,
      to: t.to === oldId ? newId : t.to,
    }));
    update({
      states,
      transitions,
      initial: def.initial === oldId ? newId : def.initial,
    });
  };

  const addState = () => {
    const n = def.states.length + 1;
    let id = `step_${n}`;
    while (def.states.some((s) => s.id === id)) id = `${id}_x`;
    const newState: WorkflowState = { id, label: `New step ${n}`, kind: "task" };
    // Insert before the first terminal so the spine reads naturally
    const terminalIdx = def.states.findIndex((s) => s.kind === "terminal");
    const states = [...def.states];
    states.splice(terminalIdx >= 0 ? terminalIdx : states.length, 0, newState);
    update({ states });
    setExpandedState(id);
  };

  const removeState = (index: number) => {
    const removed = def.states[index];
    update({
      states: def.states.filter((_, i) => i !== index),
      transitions: def.transitions.filter((t) => t.from !== removed.id && t.to !== removed.id),
      initial: def.initial === removed.id ? (def.states[0]?.id === removed.id ? def.states[1]?.id ?? "" : def.initial) : def.initial,
    });
  };

  const moveState = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= def.states.length) return;
    const states = [...def.states];
    const [item] = states.splice(index, 1);
    states.splice(target, 0, item);
    update({ states });
  };

  const setSessionState = (index: number, on: boolean) => {
    const states = def.states.map((s, i) => {
      if (i === index) return { ...s, ui: on ? ("pathway-session" as const) : undefined };
      // At most one session host
      return on && s.ui === "pathway-session" ? { ...s, ui: undefined } : s;
    });
    update({ states });
  };

  const addTransition = (fromId: string) => {
    const fallback = def.states.find((s) => s.id !== fromId);
    const t: WorkflowTransition = {
      id: `t_${fromId}_${def.transitions.length + 1}`,
      from: fromId,
      to: fallback?.id ?? fromId,
      label: "Continue",
      kind: "advance",
    };
    update({ transitions: [...def.transitions, t] });
  };

  const updateTransition = (index: number, patch: Partial<WorkflowTransition>) => {
    update({ transitions: def.transitions.map((t, i) => (i === index ? { ...t, ...patch } : t)) });
  };

  const removeTransition = (index: number) => {
    update({ transitions: def.transitions.filter((_, i) => i !== index) });
  };

  const setGuard = (index: number, guardType: string) => {
    const t = def.transitions[index];
    let guard: WorkflowGuard | undefined;
    if (guardType === "manual") guard = { type: "manual" };
    else if (guardType === "aiReview") guard = { type: "aiReview" };
    else if (guardType === "field") guard = { type: "field", field: allCaptureFields[0]?.id ?? "", op: "notEmpty" };
    else guard = undefined;
    updateTransition(index, { guard });
  };

  const addCaptureField = (stateIndex: number) => {
    const st = def.states[stateIndex];
    const capture = [...(st.capture ?? [])];
    let id = `field_${capture.length + 1}`;
    while (capture.some((c) => c.id === id)) id = `${id}_x`;
    capture.push({ id, label: "New field", type: "text" });
    updateState(stateIndex, { capture });
  };

  const updateCaptureField = (stateIndex: number, fieldIndex: number, patch: Partial<WorkflowCaptureField>) => {
    const st = def.states[stateIndex];
    const capture = (st.capture ?? []).map((c, i) => (i === fieldIndex ? { ...c, ...patch } : c));
    updateState(stateIndex, { capture });
  };

  const removeCaptureField = (stateIndex: number, fieldIndex: number) => {
    const st = def.states[stateIndex];
    updateState(stateIndex, { capture: (st.capture ?? []).filter((_, i) => i !== fieldIndex) });
  };

  const allCaptureFields = def.states.flatMap((s) => s.capture ?? []);

  // ── JSON mode ──
  const openJson = () => {
    setJsonDraft(JSON.stringify(def, null, 2));
    setJsonError(null);
    setShowJson(true);
  };
  const applyJson = () => {
    try {
      const parsed = JSON.parse(jsonDraft ?? "");
      if (!isExecutableWorkflow(parsed)) throw new Error('must be an object with "version": 2');
      const issues = validateWorkflow(parsed);
      setJsonError(issues.length > 0 ? issues.join("; ") : null);
      onChange(parsed);
      if (issues.length === 0) setShowJson(false);
    } catch (e: any) {
      setJsonError(`Invalid JSON: ${e.message}`);
    }
  };

  return (
    <Form.Group className="mb-3 wfb">
      <div className="cs-sect-head">
        <span className="cs-sect-title">Executable Workflow</span>
        <span className="cs-specref" title="SynerGen LMS Implementation Plan2-CWaves.pdf — section 8.3">§8.3</span>
        <span className="cs-sect-hint">the state machine iLearn runs — states, guarded transitions, safety exits, capture</span>
        <div className="d-flex gap-2" style={{ marginLeft: "auto" }}>
          <Button type="button" size="sm" variant="secondary" onClick={() => (showJson ? setShowJson(false) : openJson())}>
            {showJson ? "Builder view" : "JSON view"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              if (window.confirm("Revert to a simple display-only list? States, transitions, guards and capture fields will be lost."))
                onChange(downgradeToLegacy(def));
            }}
          >
            Revert to simple list
          </Button>
        </div>
      </div>

      {/* Live preview: the spine plus branch/hold/escalate edges */}
      <div className="wfb-preview">
        {def.states.map((s, i) => {
          const outs = def.transitions.filter((t) => t.from === s.id);
          const nextId = def.states[i + 1]?.id;
          const branchEdges = outs.filter((t) => !(t.kind === "advance" && t.to === nextId));
          return (
            <React.Fragment key={s.id}>
              <div
                className={
                  "wfb-node" +
                  (s.kind === "terminal" ? " wfb-node-terminal" : "") +
                  (s.kind === "hold" ? " wfb-node-hold" : "") +
                  (s.id === def.initial ? " wfb-node-initial" : "") +
                  (s.ui === "pathway-session" ? " wfb-node-session" : "")
                }
                title={s.description || s.id}
                onClick={() => setExpandedState(expandedState === s.id ? null : s.id)}
              >
                <div className="wfb-node-label">{s.label || s.id}</div>
                {s.actor && <div className="wfb-node-actor">{s.actor}</div>}
                {branchEdges.length > 0 && (
                  <div className="wfb-node-edges">
                    {branchEdges.map((t, ti) => {
                      const badge = TRANSITION_BADGE[t.kind];
                      const toState = def.states.find((x) => x.id === t.to);
                      return (
                        <span key={ti} className={"wfb-edge " + badge.cls} title={t.label + (t.guard ? ` [guard: ${t.guard.type}]` : "")}>
                          {badge.icon} {toState?.label ?? t.to}
                          {t.guard ? " 🔒" : ""}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
              {i < def.states.length - 1 && <div className="wfb-arrow">→</div>}
            </React.Fragment>
          );
        })}
      </div>

      {errors.length > 0 && (
        <div className="wfb-errors">
          <strong>{errors.length} issue{errors.length > 1 ? "s" : ""} to fix before this machine can run:</strong>
          <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      {showJson ? (
        <div className="mt-2">
          <Form.Control
            as="textarea"
            rows={16}
            value={jsonDraft ?? ""}
            onChange={(e) => setJsonDraft(e.target.value)}
            style={{ fontFamily: "monospace", fontSize: 12 }}
          />
          {jsonError && <div className="wfb-errors mt-2">{jsonError}</div>}
          <Button type="button" size="sm" variant="primary" className="mt-2" onClick={applyJson}>
            Apply JSON
          </Button>
        </div>
      ) : (
        <>
          <div className="d-flex align-items-center gap-2 mt-2 mb-2">
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase" }}>Initial state</span>
            <Form.Select
              size="sm"
              style={{ maxWidth: 260 }}
              value={def.initial}
              onChange={(e) => update({ initial: e.target.value })}
            >
              {def.states.map((s) => (
                <option key={s.id} value={s.id}>{s.label || s.id}</option>
              ))}
            </Form.Select>
            <Button type="button" size="sm" variant="secondary" className="ms-auto" onClick={addState}>
              + Add state
            </Button>
          </div>

          <div className="wfb-states">
            {def.states.map((s, si) => {
              const outs = def.transitions
                .map((t, ti) => ({ t, ti }))
                .filter(({ t }) => t.from === s.id);
              const open = expandedState === s.id;
              return (
                <div key={s.id} className={"wfb-state-card" + (open ? " open" : "")}>
                  <div className="wfb-state-head" onClick={() => setExpandedState(open ? null : s.id)}>
                    <span className="wfb-state-kind">{s.kind}</span>
                    <span className="wfb-state-title">{s.label || s.id}</span>
                    {s.ui === "pathway-session" && <span className="wfb-session-pill">pathway session</span>}
                    {s.ui === "medication-decision" && <span className="wfb-session-pill">§9 med support</span>}
                    {s.decisionStep !== undefined && (
                      <span className="wfb-step-pill" title={decisionSteps?.find((d) => d.step === s.decisionStep)?.name}>
                        step {s.decisionStep}
                      </span>
                    )}
                    <span className="wfb-state-meta">
                      {(s.capture ?? []).length > 0 && `${(s.capture ?? []).length} field${(s.capture ?? []).length > 1 ? "s" : ""} · `}
                      {outs.length} out
                    </span>
                    <span className="wfb-state-tools" onClick={(e) => e.stopPropagation()}>
                      <button type="button" title="Move up" disabled={si === 0} onClick={() => moveState(si, -1)}>↑</button>
                      <button type="button" title="Move down" disabled={si === def.states.length - 1} onClick={() => moveState(si, 1)}>↓</button>
                      <button type="button" title="Delete state" className="wfb-del" disabled={def.states.length <= 2} onClick={() => removeState(si)}>×</button>
                    </span>
                  </div>

                  {open && (
                    <div className="wfb-state-body">
                      <div className="wfb-grid-2">
                        <div>
                          <label>Label</label>
                          <Form.Control
                            size="sm"
                            value={s.label}
                            onChange={(e) => {
                              updateState(si, { label: e.target.value });
                            }}
                            onBlur={() => {
                              // Auto-derive the id from the label for fresh states
                              if (/^step_\d+/.test(s.id) && s.label) renameStateId(si, slugifyStateId(s.label, si));
                            }}
                          />
                        </div>
                        <div>
                          <label>Id <small>(stable key)</small></label>
                          <Form.Control size="sm" value={s.id} onChange={(e) => renameStateId(si, e.target.value.trim())} />
                        </div>
                        <div>
                          <label>Kind</label>
                          <Form.Select size="sm" value={s.kind} onChange={(e) => updateState(si, { kind: e.target.value as StateKind })}>
                            {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </Form.Select>
                        </div>
                        <div>
                          <label>Actor <small>(who performs this step)</small></label>
                          {(actorOptions ?? []).length > 0 ? (() => {
                            const opts = Array.from(new Set(["system", ...(actorOptions ?? [])]));
                            return (
                              <Form.Select size="sm" value={s.actor || ""} onChange={(e) => updateState(si, { actor: e.target.value || undefined })}>
                                <option value="">— unassigned —</option>
                                {s.actor && !opts.includes(s.actor) && <option value={s.actor}>{s.actor} ⚠ (not in roles master)</option>}
                                {opts.map((r) => <option key={r} value={r}>{r}</option>)}
                              </Form.Select>
                            );
                          })() : (
                            <Form.Control size="sm" value={s.actor || ""} placeholder="e.g. pharmacist / system" onChange={(e) => updateState(si, { actor: e.target.value || undefined })} />
                          )}
                        </div>
                        {(decisionSteps ?? []).length > 0 && (
                          <div>
                            <label>Implements decision step <small>(§8.3 traceability — feeds the derived map)</small></label>
                            <Form.Select
                              size="sm"
                              value={s.decisionStep ?? ""}
                              onChange={(e) => updateState(si, { decisionStep: e.target.value === "" ? undefined : Number(e.target.value) })}
                            >
                              <option value="">— not part of the sequence —</option>
                              {decisionSteps!.map((d) => (
                                <option key={d.step} value={d.step}>{d.step}. {d.name}</option>
                              ))}
                            </Form.Select>
                          </div>
                        )}
                      </div>
                      <div className="mt-2">
                        <label>Description <small>(shown to the learner in this step)</small></label>
                        <Form.Control as="textarea" rows={2} style={{ fontSize: 12 }} value={s.description || ""} onChange={(e) => updateState(si, { description: e.target.value || undefined })} />
                      </div>
                      <div className="wfb-toggle-row">
                        <Form.Check
                          type="switch"
                          id={`wfb-session-${s.id}`}
                          label="Hosts the pathway session (explore → compare → decide → score happens in this step)"
                          checked={s.ui === "pathway-session"}
                          onChange={(e) => setSessionState(si, e.target.checked)}
                        />
                        <Form.Check
                          type="switch"
                          id={`wfb-meddec-${s.id}`}
                          label="Shows §9 medication decision support (matrix rules evaluated against this case's captured data)"
                          checked={s.ui === "medication-decision"}
                          onChange={(e) => updateState(si, { ui: e.target.checked ? "medication-decision" : undefined })}
                        />
                      </div>

                      {/* Capture fields */}
                      <div className="wfb-subhead">
                        Capture fields <small>(data recorded in this step — guards can check them)</small>
                        <Button type="button" size="sm" variant="secondary" onClick={() => addCaptureField(si)}>+ Field</Button>
                      </div>
                      {(s.capture ?? []).map((c, ci) => (
                        <div key={ci} className="wfb-capture-row">
                          <Form.Control size="sm" style={{ flex: 2 }} value={c.label} placeholder="Label" onChange={(e) => updateCaptureField(si, ci, { label: e.target.value })} />
                          <Form.Control size="sm" style={{ flex: 1.4 }} value={c.id} placeholder="id" onChange={(e) => updateCaptureField(si, ci, { id: e.target.value.trim() })} />
                          <Form.Select size="sm" style={{ flex: 1.2 }} value={c.type} onChange={(e) => updateCaptureField(si, ci, { type: e.target.value as CaptureType })}>
                            {CAPTURE_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                          </Form.Select>
                          {(c.type === "select" || c.type === "multiselect") && (
                            <Form.Control size="sm" style={{ flex: 2 }} value={(c.options ?? []).join(", ")} placeholder="options, comma-separated" onChange={(e) => updateCaptureField(si, ci, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} />
                          )}
                          <Form.Check type="checkbox" label="req" checked={!!c.required} onChange={(e) => updateCaptureField(si, ci, { required: e.target.checked || undefined })} style={{ fontSize: 11 }} />
                          <button type="button" className="wfb-del" onClick={() => removeCaptureField(si, ci)}>×</button>
                        </div>
                      ))}

                      {/* Transitions out of this state */}
                      <div className="wfb-subhead">
                        Transitions from this step
                        <Button type="button" size="sm" variant="secondary" disabled={s.kind === "terminal"} onClick={() => addTransition(s.id)}>+ Transition</Button>
                      </div>
                      {s.kind === "terminal" && <div className="wfb-hint">Terminal states end the workflow — no outgoing transitions.</div>}
                      {outs.map(({ t, ti }) => (
                        <div key={ti} className="wfb-transition-card">
                          <div className="wfb-capture-row">
                            <Form.Control size="sm" style={{ flex: 2 }} value={t.label} placeholder="Button label" onChange={(e) => updateTransition(ti, { label: e.target.value })} />
                            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>to</span>
                            <Form.Select size="sm" style={{ flex: 1.6 }} value={t.to} onChange={(e) => updateTransition(ti, { to: e.target.value })}>
                              {def.states.filter((x) => x.id !== s.id).map((x) => <option key={x.id} value={x.id}>{x.label || x.id}</option>)}
                            </Form.Select>
                            <Form.Select size="sm" style={{ flex: 1.2 }} value={t.kind} onChange={(e) => updateTransition(ti, { kind: e.target.value as TransitionKind })}>
                              {TRANSITION_KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                            </Form.Select>
                            <Form.Select size="sm" style={{ flex: 1.3 }} value={t.guard?.type ?? "none"} onChange={(e) => setGuard(ti, e.target.value)} title="Guard — server-enforced condition">
                              <option value="none">No guard</option>
                              <option value="manual">Manual (authorized click)</option>
                              <option value="field">Field condition</option>
                              <option value="aiReview">Human review ack</option>
                            </Form.Select>
                            <button type="button" className="wfb-del" onClick={() => removeTransition(ti)}>×</button>
                          </div>
                          {t.guard?.type === "field" && (
                            <div className="wfb-capture-row" style={{ marginTop: 6 }}>
                              <span className="wfb-guard-label">Allow only when</span>
                              <Form.Select size="sm" style={{ flex: 1.5 }} value={t.guard.field} onChange={(e) => updateTransition(ti, { guard: { ...t.guard as any, field: e.target.value } })}>
                                <option value="">— field —</option>
                                {allCaptureFields.map((f) => <option key={f.id} value={f.id}>{f.id}</option>)}
                              </Form.Select>
                              <Form.Select size="sm" style={{ flex: 1.2 }} value={t.guard.op} onChange={(e) => updateTransition(ti, { guard: { ...t.guard as any, op: e.target.value as FieldOp } })}>
                                {FIELD_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                              </Form.Select>
                              {t.guard.op !== "notEmpty" && (
                                <Form.Control
                                  size="sm"
                                  style={{ flex: 1.5 }}
                                  value={Array.isArray(t.guard.value) ? t.guard.value.join(", ") : String(t.guard.value ?? "")}
                                  placeholder={t.guard.op === "in" ? "a, b, c" : "value"}
                                  onChange={(e) => {
                                    const fg = t.guard as FieldGuard;
                                    const raw = e.target.value;
                                    const value = fg.op === "in"
                                      ? raw.split(",").map((v) => v.trim()).filter(Boolean)
                                      : (raw !== "" && !isNaN(Number(raw)) ? Number(raw) : raw);
                                    updateTransition(ti, { guard: { ...fg, value } });
                                  }}
                                />
                              )}
                            </div>
                          )}
                          {t.guard?.type === "manual" && (
                            <div className="wfb-capture-row" style={{ marginTop: 6 }}>
                              <span className="wfb-guard-label">Roles allowed <small>(optional, comma-separated — empty = anyone)</small></span>
                              <Form.Control
                                size="sm"
                                style={{ flex: 2 }}
                                value={(t.guard.roles ?? []).join(", ")}
                                placeholder="pharmacist, physician"
                                onChange={(e) => updateTransition(ti, { guard: { type: "manual", roles: e.target.value.split(",").map((r) => r.trim()).filter(Boolean) } })}
                              />
                            </div>
                          )}
                          {t.guard?.type === "aiReview" && (
                            <div className="wfb-hint" style={{ marginTop: 6 }}>
                              The learner must explicitly acknowledge human review before this transition is allowed. AI output cannot advance the machine on its own.
                            </div>
                          )}
                          {t.guard && (guardrailOptions ?? []).length > 0 && (
                            <div className="wfb-guard-row" style={{ marginTop: 6 }}>
                              <span className="wfb-guard-label" title="§18.1 — declare which §10.2 guardrail claim this guard implements; the guardrail table derives its Enforced-by from these declarations">
                                Implements guardrail
                              </span>
                              <Form.Select size="sm" style={{ flex: 2 }} value={t.implementsGuardrail || ""}
                                onChange={(e) => updateTransition(ti, { implementsGuardrail: e.target.value || undefined })}>
                                <option value="">— none —</option>
                                {guardrailOptions!.map((g) => (
                                  <option key={g.id} value={g.id}>⛨ {g.trigger.length > 48 ? g.trigger.slice(0, 48) + "…" : g.trigger}</option>
                                ))}
                              </Form.Select>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Form.Group>
  );
};

export default WorkflowBuilder;
